import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import API from "../services/API";
import { getThumbnail as getContentThumbnail } from "../services/Content";

export const ContentContext = createContext(null);

/**
 * Provider component that holds and manages content items (systems, elements,
 * environments, collections) in state.
 * 
 * OPTIMIZATION: Pre-fetches all environments on mount so that individual lookups
 * don't require separate API calls. This significantly speeds up pages like
 * Dashboard and QuestionAnswersPage that need environment names.
 */
export const ContentProvider = ({ children }) => {
  // contentState will store items in an object keyed by "contentType-itemId".
  // e.g., { "systems-123": {...}, "elements-456": {...}, "environments-789": {...}, "collections-012": {...} }
  const [contentState, setContentState] = useState({});
  // State for systems, elements, environments, and collections.
  const [mySystems, setMySystems] = useState([]);
  const [myElements, setMyElements] = useState([]);
  const [myEnvironments, setMyEnvironments] = useState([]);
  const [myCollections, setMyCollections] = useState([]);
  const [allEnvironments, setAllEnvironments] = useState([]);
  
  // Loading states for better UX
  const [environmentsLoading, setEnvironmentsLoading] = useState(true);
  const [systemsLoading, setSystemsLoading] = useState(true);
  const [elementsLoading, setElementsLoading] = useState(true);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [collectionsLoaded, setCollectionsLoaded] = useState(false);

  // Helper to build a unique key from contentType and itemId.
  const makeKey = (contentType, itemId) => `${contentType}-${itemId}`;

  /**
   * OPTIMIZATION: Create a lookup map from ALL environments array.
   * This allows O(1) lookups by environment ID without API calls.
   */
  const environmentLookup = useMemo(() => {
    const lookup = {};
    allEnvironments.forEach((env) => {
      if (env.id) {
        lookup[env.id] = env;
        // Also cache in contentState format for compatibility
        const key = makeKey("environments", env.id);
        lookup[key] = env;
      }
    });
    return lookup;
  }, [allEnvironments]);

  /**
   * Fetch an item from the API if it isn't already in our state.
   * 
   * OPTIMIZATION: For environments, check the pre-fetched lookup first.
   * The second parameter `requireAuth` is now optional - defaults to true
   * but can be set to false for public content.
   */
  const getItem = useCallback(
    async (contentType, itemId, requireAuth = true) => {
      const key = makeKey(contentType, itemId);
      
      // First check our cached state
      const cachedItem = contentState[key];
      if (cachedItem) {
        return cachedItem;
      }

      // OPTIMIZATION: For environments, check the pre-fetched lookup
      if (contentType === "environments" && environmentLookup[itemId]) {
        return environmentLookup[itemId];
      }

      // Fall back to API call
      try {
        const response = await API.get(`${contentType}/${itemId}`, {
          requiresAuth: requireAuth,
        });

        console.log("ELEMENT RESPONSE", response);
        const data = response.data;

        setContentState((prev) => ({
          ...prev,
          [key]: data,
        }));

        return data;
      } catch (error) {
        console.error(`Error fetching ${contentType}/${itemId}:`, error);
        return null;
      }
    },
    [contentState, environmentLookup]
  );

  /**
   * OPTIMIZATION: Synchronous lookup for environments from pre-fetched data.
   * Returns null if not found (no API call).
   */
  const getEnvironmentSync = useCallback(
    (environmentId) => {
      if (!environmentId) return null;
      return environmentLookup[environmentId] || null;
    },
    [environmentLookup]
  );

  /**
   * OPTIMIZATION: Get environment name directly without async call.
   * Falls back to the ID if name isn't available.
   */
  const getEnvironmentName = useCallback(
    (environmentId) => {
      if (!environmentId) return "Unknown";
      const env = environmentLookup[environmentId];
      return env?.name || environmentId;
    },
    [environmentLookup]
  );

  /**
   * Helper to form a thumbnail URL based on the item id.
   */
  const getThumbnail = useCallback((id) => {
    return getContentThumbnail(id);
  }, []);

  /**
   * Fetch all systems with the query param mine=true.
   */
  const fetchSystems = async () => {
    setSystemsLoading(true);
    try {
      const response = await API.get("systems?mine=true", { requiresAuth: true });
      console.log("SYSTEMS", response);
      setMySystems(response.data || []);
    } catch (error) {
      console.error("Error fetching systems:", error);
    } finally {
      setSystemsLoading(false);
    }
  };

  /**
   * Fetch all elements with query params.
   */
  const fetchElements = async () => {
    setElementsLoading(true);
    try {
      const response = await API.get(
        "elements?version=6000&platform=android&runtimePlatform=windows&mine=true",
        { requiresAuth: true }
      );
      console.log("ELEMENTS", response);
      setMyElements(response.data || []);
    } catch (error) {
      console.error("Error fetching elements:", error);
    } finally {
      setElementsLoading(false);
    }
  };

  /**
   * Fetch all environments (for lookups) AND my environments (for the page).
   * 
   * OPTIMIZATION: Fetch ALL environments to support environment name lookups
   * for any assessment, but also fetch mine=true for the EnvironmentsPage list.
   */
  const fetchEnvironments = async () => {
    setEnvironmentsLoading(true);
    try {
      // Fetch ALL environments for lookups (Dashboard, Session page, etc.)
      const allResponse = await API.get("environments", {
        requiresAuth: true,
      });
      console.log("ALL ENVIRONMENTS", allResponse);
      setAllEnvironments(allResponse.data || []);
      
      // Also populate contentState with all environments for instant lookups
      if (allResponse.data && Array.isArray(allResponse.data)) {
        const newContentState = {};
        allResponse.data.forEach((env) => {
          if (env.id) {
            newContentState[makeKey("environments", env.id)] = env;
          }
        });
        setContentState((prev) => ({
          ...prev,
          ...newContentState,
        }));
      }

      // Fetch MY environments for the EnvironmentsPage list
      const myResponse = await API.get("environments?mine=true", {
        requiresAuth: true,
      });
      console.log("MY ENVIRONMENTS", myResponse);
      setMyEnvironments(myResponse.data || []);
    } catch (error) {
      console.error("Error fetching environments:", error);
    } finally {
      setEnvironmentsLoading(false);
    }
  };

  /**
   * Fetch my collections for CollectionsPage.
   *
   * If collections have already been fetched once, this returns cached values
   * unless force=true is provided.
   */
  const fetchCollections = useCallback(
    async (force = false) => {
      if (!force && collectionsLoaded) {
        return myCollections;
      }

      setCollectionsLoading(true);
      try {
        const response = await API.get("collections?mine=true", {
          requiresAuth: true,
        });

        const collections = Array.isArray(response.data) ? response.data : [];
        setMyCollections(collections);
        setCollectionsLoaded(true);

        if (collections.length > 0) {
          const newContentState = {};
          collections.forEach((collection) => {
            if (collection?.id) {
              newContentState[makeKey("collections", collection.id)] = collection;
            }
          });
          setContentState((prev) => ({
            ...prev,
            ...newContentState,
          }));
        }

        return collections;
      } catch (error) {
        console.error("Error fetching collections:", error);
        return [];
      } finally {
        setCollectionsLoading(false);
      }
    },
    [collectionsLoaded, myCollections]
  );

  // Fetch systems, elements, environments, and collections when the provider mounts.
  useEffect(() => {
    fetchSystems();
    fetchElements();
    fetchEnvironments();
    fetchCollections();
    // Run once on provider mount; this matches existing content bootstrapping behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = {
    // Original API
    getItem,
    getThumbnail,
    mySystems,
    fetchSystems,
    myElements,
    fetchElements,
    myEnvironments,
    allEnvironments,
    fetchEnvironments,
    myCollections,
    fetchCollections,
    
    // NEW: Optimized environment lookups
    getEnvironmentSync,
    getEnvironmentName,
    environmentLookup,
    
    // NEW: Loading states
    environmentsLoading,
    systemsLoading,
    elementsLoading,
    collectionsLoading,
  };

  return (
    <ContentContext.Provider value={contextValue}>
      {children}
    </ContentContext.Provider>
  );
};

/**
 * Custom hook for accessing our content context.
 */
export const useContent = () => useContext(ContentContext);
